import { ITEM_CHARGE_TYPE, FREIGHT_MODE, RATE_CARD_TRUCK_SIZE, CARGO_TYPE } from '@types';

export type TRateCardModifier = {
	id: number;
	name: string;
	label: string;
	value: number;
	type: string;
}

export type TRateCardModifierGroup = {
	name: string;
	label: string;
	modifiers: TRateCardModifier[]
}

export type TRateCardRules = {
	allow_weekend: boolean;
	book_start_at_days: number;
	max_future_book_at_days: number;
	allow_pickup_stops: boolean;
	allow_consolidation: boolean;
	disable_take_now_price: boolean;
	allow_address_change: boolean;
	min_drop_at_days: number;
	booking_cutoff: string | null;
	cpk_config_base_price: number | null;
	cpk_config_cubic_conversion: number | null;
	cpk_config_pallet_height: number | null;
	cpk_config_pallet_width: number | null;
	cpk_config_pallet_length: number | null;
	min_charge: string | number;
}

export type TRateCardFuelLevy = {
	id: string | null | undefined;
	type: string;
	name?: string | null;
	customer_external_id: null | string;
	current?: {
		id: string | undefined;
		start_at: string;
		tariff: number | null;
		tariff_type: string;
	},
	next?: {
		id: string;
		start_at: string;
		tariff: number | null;
		tariff_type: string;
	} | null;
	updated_at: string;
}

export type TRateCardFuelLevies = {
	global_shipper?: TRateCardFuelLevy;
	global_carrier?: TRateCardFuelLevy;
	customer_levies: TRateCardFuelLevy[];
	rate_card?: TRateCardFuelLevy | null;
}

export type TRateCardFuelLeviesResponse = Omit<Partial<TRateCardFuelLevies>, 'customer_levies'> & {
	customer?: TRateCardFuelLevy | null;
	customer_levies?: TRateCardFuelLevy[] | null;
}

export type TFuelLevyType = 'global_shipper' | 'global_carrier' | 'customer' | 'rate_card';

export enum TRateCardModifierType {
	PER_LIFT = 'PER_LIFT',
	PER_STOP = 'PER_STOP',
	PERCENT = 'PERCENT',
	FIXED = 'FIXED',
	PER_HOUR = 'PER_HOUR',
	THRESHOLD_KG = 'THRESHOLD_KG',
	PER_DAY = 'PER_DAY',
	PER_PALLET = 'PER_PALLET',
}

export type TRateCardPerStopModifier = {
	type: TRateCardModifierType.PER_STOP,
	value: string;
	attracts_fuel_levy?: boolean;
}

export type TSplitTrailerRate = {
	value: string;
}

export type TSplitTrailerModifier = {
	type: TRateCardModifierType.PER_STOP;
	truck_sizes: {
		A_DOUBLE: TSplitTrailerRate;
		B_DOUBLE: TSplitTrailerRate;
		B_TRIPLE: TSplitTrailerRate;
	};
	attracts_fuel_levy?: boolean;
}

export type TRateCardHandUnloadModifier = {
	type: TRateCardModifierType.PER_STOP | TRateCardModifierType.PER_PALLET,
	value: string;
	attracts_fuel_levy?: boolean;
}

export type TRateCardTailgateLiftModifier = {
	type: TRateCardModifierType.PER_LIFT | TRateCardModifierType.PER_STOP,
	value: string;
	pallets_per_lift?: string;
	max_charge?: string
	attracts_fuel_levy?: boolean;
}

export type TRateCardHolidaySurcharge = {
	type: TRateCardModifierType.PERCENT | TRateCardModifierType.PER_DAY;
	value: string;
	attracts_fuel_levy?: boolean;
};

export type TSimpleModifier = {
	type: TRateCardModifierType.FIXED;
	value: string,
	attracts_fuel_levy?: boolean;
}

export type TTruckTypeModifierDetails = {
	TIPPER: TSimpleModifier;
	TAUTLINER: TSimpleModifier;
	TAUTLINER_DROP_DECK: TSimpleModifier;
	TAUTLINER_MEZZANINE_DECK: TSimpleModifier;
	TAUTLINER_STRAIGHT_DECK: TSimpleModifier;
	PANTECH: TSimpleModifier;
	FLAT_TOP: TSimpleModifier;
	SIDELOADER: TSimpleModifier;
	SKELETON_TRAILER: TSimpleModifier;
	OPEN_TOP: TSimpleModifier;
	UTE: TSimpleModifier;
}

export type TTruckTypeModifier = {
	details: TTruckTypeModifierDetails;
	attracts_fuel_levy?: boolean;
}

export type TRateCardDaySurcharges = {
	saturday: TRateCardHolidaySurcharge | null;
	sunday: TRateCardHolidaySurcharge | null;
	public_holiday: TRateCardHolidaySurcharge | null;
	attracts_fuel_levy?: boolean;
}

export type TDangerousGoodsModifier = {
	type: TRateCardModifierType.FIXED | TRateCardModifierType.PERCENT;
	value: string;
	attracts_fuel_levy?: boolean;
} | {
	type: TRateCardModifierType.THRESHOLD_KG;
	value_below: string;
	value_above: string;
	threshold_kg: string;
	attracts_fuel_levy?: boolean;
};

export type TRateCardModifierSet = {
	pallet_jack: TSimpleModifier,
	pallet_exchange: TSimpleModifier,
	pallet_transfer: TSimpleModifier,
	tailgate_lift: TRateCardTailgateLiftModifier | null;
	day_surcharges: TRateCardDaySurcharges;
	time_slot: TRateCardPerStopModifier | null;
	hand_unload: TRateCardHandUnloadModifier | null;
	split_trailer: TSplitTrailerModifier | null;
	truck_type: TTruckTypeModifier;
	tolls: TSimpleModifier;
	admin_fee: TSimpleModifier;
	additional_pickup_stops: {
		type: TRateCardModifierType.FIXED | TRateCardModifierType.PER_STOP;
		value: string;
		threshold: number;
		attracts_fuel_levy?: boolean;
	} | null;
	additional_drop_off_stops: {
		type: TRateCardModifierType.FIXED | TRateCardModifierType.PER_STOP;
		value: string;
		threshold: number;
		attracts_fuel_levy?: boolean;
	} | null;
	dangerous_goods: TDangerousGoodsModifier | null;
	admin_standard_booking_fee?: TSimpleModifier;
}

export type RateCardType = 'WEIGHT' | 'PALLETS' | 'FULL_LOAD' | 'CPK' | 'ITEM';

export type TRateCardItemDefinition = {
	item_id: string;
	is_active: boolean;
	name: string;
	// The item cargo type this rate card prices (ITEM_DEFINITION_CARGO_TYPES); unset on items saved before it existed.
	cargo_type?: CARGO_TYPE;
	width_cm: string;
	length_cm: string;
	height_cm: string;
	weight_kg: string;
	charge_type: ITEM_CHARGE_TYPE;
}

export type TSettingsRateCardType = {
	type: RateCardType;
	ranges: {
		end: number;
		start: number;
	}[];
	is_editable: boolean;
	modifier_set: TRateCardModifierSet;
	rules: TRateCardRules;
	item_definition?: TRateCardItemDefinition;
}


export type TRateCardSettings = {
    settings: {
		preexisting: boolean;
        state: string;
        full_load: RATE_CARD_TRUCK_SIZE[];
        fuel_levy_type: TFuelLevyType;
		fuel_levy_id?: string;
		rate_card_types: TSettingsRateCardType[];
		disable_admin_rate_card_price: boolean;
		use_pallet_quantity_for_pricing: boolean;
    },
    customer_type: string;
    permissions: string[];
    fuel_levies: TRateCardFuelLevies;
}

export type TRateCardSettingsPayload = {
	disable_admin_rate_card_price: boolean;
	use_pallet_quantity_for_pricing: boolean;
	fuel_levy_type: TFuelLevyType;
	fuel_levy_id?: string;
	fuel_levy_next_value: number | null;
	fuel_levy_next_start_at: string | null;
	full_load: RATE_CARD_TRUCK_SIZE[];
	rate_card_types: TSettingsRateCardType[];
}

export type TTempRage = {
	Frozen?: number;
	Chilled?: number;
	Ambient?: number;
}

export type TFullLoadRangePrices = {
	B_DOUBLE: TTempRage;
	B_DOUBLE_HIGH_CUBE: TTempRage;
	B_TRIPLE: TTempRage;
	CONTAINER_20_FT_COASTAL: TTempRage;
	CONTAINER_40_FT_COASTAL: TTempRage;
	CONTAINER_46_FT_COASTAL: TTempRage;
	CONTAINER_48_FT_COASTAL: TTempRage;
	RIGID: TTempRage;
	SINGLE: TTempRage;
	SUPER_B_DOUBLE: TTempRage;
	A_DOUBLE: TTempRage;
}

export enum RateCardTypeLabel {
	PALLETS = 'Pallets',
	WEIGHT = 'Weight',
	FULL_LOAD = 'Full Load',
	CPK = 'CPK',
}

export interface ILaneRateCard {
	modifier_set: TRateCardModifierSet;
	type: RateCardType;
	rangePrices: TFullLoadRangePrices | { [key: string]: TTempRage };
	margins: TTempRage;
	extra_attributes: {
		priced_annualized_volume: number | null;
		priced_margin: number | null;
	};
	rules: TRateCardRules;
	item_definition?: TRateCardItemDefinition;
}

export type TCPIIncrease = {
	id: string;
	scheduled_at: string;
	processed_at: string | null;
	cpi_percentage: string;
};

export type TSelectedLaneData = {
	fuel_levy_type: string;
	has_cpi_increase_scheduled: boolean;
	is_active: boolean;
};

export interface ILane {
	id: string;
	external_id: string | null;
	customer_external_id: string;
	customer_name: string;
	freight_mode: FREIGHT_MODE;
	origin_location: {
		text: string;
		locality_type: 'POSTCODE' | 'REGION';
	}
	destination_location: {
		text: string;
		locality_type: 'POSTCODE' | 'REGION';
	};
	// TODO remove once lanesNoEndDateFeatureFlag flag is removed
	end_date?: string;
	fuel_levy_type: string;
	nick_name: string;
	description: string | null;
	rate_cards: ILaneRateCard[]
	is_contracted: boolean | null;
	is_4pl: boolean | null;
	is_active: boolean;
	fuel_levy: TRateCardFuelLevy;
	fuel_levies: TRateCardFuelLevies;
	fuel_levy_id?: string | null;
	fuel_levy_value: number;
	rate_card_types: string[];
	modifiers_type?: string;
	allow_consolidation?: boolean;
	temperatures: string[];
	use_pallet_quantity_for_pricing: boolean;
	minimum_billable_hours: string;
	shipper_tag: {
		external_id: string;
		name: string;
	} | null;
	latest_cpi_increase?: TCPIIncrease;
	// This 3 won't be optional once the BE is ready
	created_at?: string;
	updated_at?: string;
	average_margin?: number;
	// For linked rate card
	lane_link: {
		external_id?: string;
		margin_percentage?: string
	},
	carrier?: {
		name: string;
		external_id: string;
	};
	shipper?: {
		name: string;
		external_id: string;
	};
	shipper_lane_external_id?: string;
}
export interface IRateCard_Depricated {
	id: string;
	is_active: boolean;
	is_contracted: boolean;
	mode: string;
	type: string;
	ruleset: {
		id: number;
		tariffs: {
			default: {
				value: number;
				margin: number;
			}
		},
		conditions: [
			{
				value: number[];
				matcher: string;
			}
		]
	}[];
	external_id: string;
	is_4pl: boolean;
	rate_card_locations: {
		id: string;
		action_type: string;
		locality_id: string;
		locality_type: string;
		data: {
			id: string;
			name: string;
			timezone: string;
			is_active: boolean;
			created_at: string;
			updated_at: string;
			description: string;
			country_code: string;
		}
	}[];
	lane: {
		external_id: string;
		customer_external_id: string;
		locations_id: string;
		id: string;
		end_date?: string;
		freight_mode: string;
		fuel_levy: {
			id: string;
			type: string;
			customer_external_id: null,
			updated_at: string;
		},
		fuel_levy_type: string;
		modifiers_type: string;
		rate_card_types: string[];
		temperatures: string[];
		full_load: any;
		nick_name: string;
		description: null,

		origin: null,
		destination: null,
		has_special_characteristics: null,
		is_4pl: boolean;
		shipper_tag: null,
		use_pallet_quantity_for_pricing: boolean;
		allow_consolidation: boolean;
		disable_take_now_price: boolean;
		lane_history_event: {
			external_id: string;
			has_newer_versions?: boolean;
		}
	},
	customer_external_id: string;
	is_admin: boolean;
	use_pallet_quantity_for_pricing: boolean;
	rules: null,
}

export interface RateCardHistoryItem {
	external_id: string,
	action: string,
	actioned_at: string,
	user: {
		external_id: string,
		name: string | null,
	},
	description?: string,
}

export type RateCardHistory = {
	data: RateCardHistoryItem[]
	meta: {
		total: number
	}
};

export type TFuelLevyResponse = {
    id?: string;
    start_at: string;
    tariff: number;
}

export type TScheduledFuelLevyResponse = {
    current?: TFuelLevyResponse;
    next?: TFuelLevyResponse;
}

export type TModifier = {
    id: number;
    name: string;
    label: string;
    value: number;
    type: 'percent' | 'fixed';
}

export type TGroupedModifier = {
    name: string;
    label: string;
    modifiers: TModifier[];
}

export type TLaneResourceExtended = {
    external_id: string | null;
    customer_external_id: string;
    freight_mode: FREIGHT_MODE;
	origin_location: {
		text: string;
		locality_type: 'POSTCODE' | 'REGION';
	}
	destination_location: {
		text: string;
		locality_type: 'POSTCODE' | 'REGION';
	};
    fuel_levy_type: TFuelLevyType;
    minimum_billable_hours: number;
    end_date?: string;
    nick_name: string;
    description: string;

    rate_cards: ILaneRateCard[];
    disable_take_now_price: boolean;
    has_special_characteristics: boolean;
    is_4pl: boolean;
	modifiers_type: 'customer' | 'lane' | 'rate_card';
    fuel_levies: Partial<Record<TFuelLevyType, TScheduledFuelLevyResponse>>;
    modifiers: TGroupedModifier[];
    rules?: Record<string, any>;
    use_pallet_quantity_for_pricing: boolean;
}


export type TLinkedRateCard = {
	linked_lane_external_id: string | null;
	carrier: {
		external_id: string | null;
		name: string | null;
	}
	shipper: {
		external_id: string | null;
		name: string | null;
	}
	margin_percentage: string | null;
	carrier_lane_id: string | null;
	shipper_lane_id: string | null;
}

export interface TScheduleCPIIncreaseRequest {
	lane_external_id: string;
	scheduled_at: string;
	cpi_percentage: string;
}

export interface TUpdateCPIIncreaseRequest {
	scheduled_at: string;
	cpi_percentage: string;
}

export interface TBulkScheduleCPIIncreaseRequest {
	lane_external_ids: string[];
	scheduled_at: string;
	cpi_percentage: string;
}

export interface TBulkDeleteCPIIncreaseRequest {
	lane_external_ids: string[];
}

export interface IFuelLevyHistoryEntry {
	tariff: number;
	effective_from: string;
	effective_until: string | null;
	changed_at: string;
	changed_by: string | null;
	fuel_levy_type: TFuelLevyType;
	status: 'scheduled' | 'current' | 'expired';
}
